import React, { useState, useEffect } from 'react';
import { FileText, Download, Folder, ChevronDown, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import HeaderNew from '../components/HeaderNew';
import Footer from '../components/Footer';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AllDocuments = () => {
  const [rubriques, setRubriques] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRubriques, setExpandedRubriques] = useState({});
  const [downloadingRubrique, setDownloadingRubrique] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Charger les rubriques
      const rubriquesResponse = await fetch(`${API_BASE}/api/publications/rubriques`);
      if (rubriquesResponse.ok) {
        const rubriquesData = await rubriquesResponse.json();
        setRubriques(rubriquesData.rubriques || []);
        
        // Expand all rubriques by default
        const expanded = {};
        (rubriquesData.rubriques || []).forEach(r => {
          expanded[r.id] = true;
        });
        setExpandedRubriques(expanded);
      }

      // Charger toutes les publications
      const pubsResponse = await fetch(`${API_BASE}/api/publications`);
      if (pubsResponse.ok) {
        const pubsData = await pubsResponse.json();
        setPublications(pubsData.publications || []);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRubrique = (rubriqueId) => {
    setExpandedRubriques(prev => ({
      ...prev,
      [rubriqueId]: !prev[rubriqueId]
    }));
  };

  const getPublicationsForRubrique = (rubriqueId) => {
    return publications.filter(p => p.rubrique_id === rubriqueId && p.file_path);
  };

  const handleDownloadAll = async (rubriqueId, rubriqueName) => {
    const pubs = getPublicationsForRubrique(rubriqueId);
    if (pubs.length === 0) {
      alert('Aucun document à télécharger dans cette rubrique');
      return;
    }

    setDownloadingRubrique(rubriqueId);
    try {
      const response = await fetch(
        `${API_BASE}/api/public/publications/rubriques/${rubriqueId}/download-pdfs`
      );

      if (response.ok) {
        const blob = await response.blob();
        const pdfsCount = response.headers.get('X-PDFs-Count') || pubs.length;
        
        const safeName = rubriqueName.replace(/[^a-zA-Z0-9àâäéèêëïîôùûüç\s-_]/g, '').replace(/\s+/g, '_');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${safeName}_documents.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        
        alert(`✅ ${pdfsCount} document(s) téléchargé(s) avec succès !`);
      } else {
        alert('❌ Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      alert('❌ Erreur lors du téléchargement');
    } finally {
      setDownloadingRubrique(null);
    }
  };

  const handleDownloadSingle = (publication) => {
    if (publication.full_file_url) {
      window.open(`${API_BASE}${publication.full_file_url}`, '_blank');
    } else if (publication.file_path) {
      const fileId = publication.file_path.includes('/api/files/') 
        ? publication.file_path 
        : `/api/files/${publication.file_path}`;
      window.open(`${API_BASE}${fileId}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderNew />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNew />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-gradient-to-br from-amber-800 to-amber-900"
        style={{ marginTop: '80px' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: '"Lora", serif' }}
          >
            Documents & Publications
          </h1>
          <p className="text-xl text-amber-100 max-w-2xl mx-auto">
            Retrouvez l'ensemble de nos publications : bulletins, fiches lignagères, et documents officiels des Lignages de Bruxelles.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-5xl">
          
          {rubriques.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aucune publication disponible pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {rubriques.map(rubrique => {
                const rubriquePublications = getPublicationsForRubrique(rubrique.id);
                const isExpanded = expandedRubriques[rubrique.id];
                
                if (rubriquePublications.length === 0) return null;
                
                return (
                  <div 
                    key={rubrique.id} 
                    className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100"
                  >
                    {/* Rubrique Header */}
                    <div 
                      className="flex items-center justify-between p-6 bg-gradient-to-r from-amber-50 to-amber-100 cursor-pointer hover:from-amber-100 hover:to-amber-150 transition-colors"
                      onClick={() => toggleRubrique(rubrique.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-amber-600 rounded-lg">
                          <Folder className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 
                            className="text-2xl font-bold text-amber-900"
                            style={{ fontFamily: '"Lora", serif' }}
                          >
                            {rubrique.name}
                          </h2>
                          <p className="text-amber-700 text-sm">
                            {rubriquePublications.length} document{rubriquePublications.length > 1 ? 's' : ''} disponible{rubriquePublications.length > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Download All Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadAll(rubrique.id, rubrique.name);
                          }}
                          disabled={downloadingRubrique === rubrique.id}
                          className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:bg-gray-400 font-medium shadow-md"
                        >
                          {downloadingRubrique === rubrique.id ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Téléchargement...
                            </>
                          ) : (
                            <>
                              <Download className="w-5 h-5" />
                              Tout télécharger (ZIP)
                            </>
                          )}
                        </button>
                        
                        {/* Expand/Collapse Icon */}
                        {isExpanded ? (
                          <ChevronDown className="w-6 h-6 text-amber-600" />
                        ) : (
                          <ChevronRight className="w-6 h-6 text-amber-600" />
                        )}
                      </div>
                    </div>
                    
                    {/* Publications List */}
                    {isExpanded && (
                      <div className="divide-y divide-gray-100">
                        {rubriquePublications
                          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                          .map((publication, index) => (
                            <div 
                              key={publication.id || index}
                              className="flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center gap-4 flex-1">
                                <div className="p-2 bg-red-100 rounded-lg">
                                  <FileText className="w-5 h-5 text-red-600" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-semibold text-gray-800">
                                    {publication.title || 'Document sans titre'}
                                  </h3>
                                  {publication.description && (
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                                      {publication.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              <button
                                onClick={() => handleDownloadSingle(publication)}
                                className="flex items-center gap-2 px-4 py-2 text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded-lg transition-colors font-medium"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Ouvrir
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Info Section */}
          <div className="mt-16 p-8 bg-amber-50 rounded-xl border border-amber-200">
            <h3 
              className="text-xl font-bold text-amber-900 mb-4"
              style={{ fontFamily: '"Lora", serif' }}
            >
              À propos de nos publications
            </h3>
            <p className="text-amber-800 leading-relaxed">
              Les Lignages de Bruxelles publient régulièrement des documents pour leurs membres et le public. 
              Le <strong>Bulletin</strong> est notre publication phare, relatant les activités de l'association et les recherches généalogiques. 
              Le <strong>Valet</strong> et les <strong>Filiations lignagères</strong> complètent notre collection documentaire.
            </p>
            <p className="text-amber-700 mt-4 text-sm">
              Tous les documents sont au format PDF et peuvent être téléchargés individuellement ou par rubrique complète.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllDocuments;
